import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/shared/ui';
import { ResizeHandleWithRef } from './ResizeHandleWithRef';

/** Названия границы по тому, что лежит в блоке: у видимого блока и у скрытого */
const LABEL_KEYS = {
  data: { shown: 'dialogs:resizeOrHideDialogData', hidden: 'dialogs:showDialogData' },
  textFeed: { shown: 'dialogs:textFeed.resizeOrHideFeed', hidden: 'dialogs:textFeed.showFeed' },
} as const;

type Props = {
  /** Блок данных о диалоге скрыт — его ширина сведена к нулю. */
  isDataHidden: boolean;
  /** В блоке вместо данных о диалоге — переписка текстовой коммуникации, пока панель атрибутов скрыта */
  isTextFeed?: boolean;
};

/**
 * Граница блока данных о диалоге: перетаскиванием меняет его размер, убирает с экрана и возвращает.
 *
 * Названа тем, что ею можно сделать сейчас: у видимого блока — изменить размер или скрыть,
 * у скрытого — показать, потому что граница остаётся единственным способом его вернуть.
 *
 * Граница вытянута во всю высоту панели, поэтому подсказка показывается у места наведения:
 * у края она встала бы в стороне от указателя, которым на границу навели.
 */
export const DialogDataResizeHandle: FC<Props> = ({ isDataHidden, isTextFeed = false }) => {
  const { t } = useTranslation(['dialogs']);

  const labelKeys = LABEL_KEYS[isTextFeed ? 'textFeed' : 'data'];
  const label = t(isDataHidden ? labelKeys.hidden : labelKeys.shown);

  return (
    <Tooltip tooltip={label} atPointer>
      <ResizeHandleWithRef withHandle aria-label={label} />
    </Tooltip>
  );
};
